import type React from 'react';
import {useState} from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {Add, DeleteOutline} from '@mui/icons-material';
import {errorText} from '../../hooks/notices';
import type {LineInput, LineKind, ModelInput, PriceModel, ShouldCost, Tier} from '../../types/shouldCostTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

type Props = {
    model: PriceModel;
    options: ShouldCost['options'];
    onSubmit: (change: Partial<ModelInput>) => Promise<unknown>;
    onClose: () => void;
};

type DraftLine = {id: number | null; name: string; baseCost: string; seriesId: number | ''; tier: Tier; kind: LineKind;
    assumption: string; calibrationPct: number | null};

/** Edit model (spec 4.9): name, unit, currency, base month, volume, and the cost lines with their drivers and tiers. */
export const ModelEditorDialog: React.FC<Props> = ({model, options, onSubmit, onClose}) => {
    const [meta, setMeta] = useState({name: model.name, unit: model.unit, currency: model.currency,
        baseMonth: model.baseMonth.slice(0, 7), annualVolume: String(model.annualVolume)});
    const [lines, setLines] = useState<DraftLine[]>(model.lines.map(l => ({
        id: l.id, name: l.name, baseCost: String(l.baseCost), seriesId: l.series?.id ?? '', tier: l.tier, kind: l.kind,
        assumption: l.assumption, calibrationPct: l.calibrationPct})));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const setLine = (i: number, change: Partial<DraftLine>) => setLines(lines.map((l, j) => (j === i ? {...l, ...change} : l)));
    const total = lines.reduce((t, l) => t + (Number(l.baseCost) || 0), 0);
    const ready = meta.name.trim() && lines.length > 0 && lines.every(l => l.name.trim() && Number(l.baseCost) >= 0 && l.baseCost !== '');

    const submit = () => {
        setBusy(true);
        setError(null);
        const out: LineInput[] = lines.map(l => ({id: l.id, name: l.name.trim(), baseCost: Number(l.baseCost),
            seriesId: l.seriesId === '' ? null : l.seriesId, tier: l.tier, kind: l.kind, assumption: l.assumption.trim(),
            calibrationPct: l.calibrationPct}));
        onSubmit({name: meta.name.trim(), unit: meta.unit.trim() || 'unit', currency: meta.currency.trim().toUpperCase(),
            baseMonth: `${meta.baseMonth}-01`, annualVolume: Number(meta.annualVolume) || 0, lines: out})
            .then(onClose).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="cp-edit-model-title">
            <DialogTitle id="cp-edit-model-title">Edit price model</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField label="Model name" size="small" value={meta.name} sx={{flex: 2}}
                                   onChange={e => setMeta({...meta, name: e.target.value})}/>
                        <TextField label="Unit" size="small" value={meta.unit} sx={{flex: 1}} onChange={e => setMeta({...meta, unit: e.target.value})}/>
                        <TextField label="Currency" size="small" value={meta.currency} sx={{flex: 1}}
                                   onChange={e => setMeta({...meta, currency: e.target.value})}/>
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField label="Base month" size="small" type="month" value={meta.baseMonth} sx={{flex: 1}}
                                   onChange={e => setMeta({...meta, baseMonth: e.target.value})} slotProps={{inputLabel: {shrink: true}}}/>
                        <TextField label="Annual volume" size="small" type="number" value={meta.annualVolume} sx={{flex: 1}}
                                   onChange={e => setMeta({...meta, annualVolume: e.target.value})}/>
                    </Stack>
                    <Typography variant="subtitle2">{`Cost lines · base price ${total.toLocaleString(LOCALE, {maximumFractionDigits: 4})} per ${meta.unit || 'unit'}`}</Typography>
                    {lines.map((l, i) => (
                        // Lines have no stable id until saved; the position is their identity in this form.
                        <Stack key={i} direction={{xs: 'column', md: 'row'}} gap={1} alignItems={{md: 'center'}} flexWrap="wrap"
                               sx={{pb: 1, borderBottom: '1px solid', borderColor: 'divider'}}>
                            <TextField label="Line item" size="small" value={l.name} sx={{flex: 2}}
                                       onChange={e => setLine(i, {name: e.target.value})} slotProps={{htmlInput: {maxLength: 120}}}/>
                            <TextField label="Base cost" size="small" type="number" value={l.baseCost} sx={{flex: 1}}
                                       onChange={e => setLine(i, {baseCost: e.target.value})}/>
                            <TextField select label="Cost driver" size="small" value={l.seriesId} sx={{flex: 2}}
                                       onChange={e => {
                                           const id = e.target.value === '' ? '' : Number(e.target.value);
                                           const series = options.series.find(s => s.id === id);
                                           setLine(i, {seriesId: id, tier: series ? series.tier : 'estimated'});
                                       }}>
                                <MenuItem value="">None (fixed)</MenuItem>
                                {options.series.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </TextField>
                            <TextField select label="Tier" size="small" value={l.tier} sx={{flex: 1}}
                                       onChange={e => setLine(i, {tier: e.target.value as Tier})}>
                                {options.tiers.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                            </TextField>
                            <TextField select label="Kind" size="small" value={l.kind} sx={{flex: 1}}
                                       onChange={e => setLine(i, {kind: e.target.value as LineKind})}>
                                {options.kinds.map(k => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
                            </TextField>
                            <TextField label="Assumption" size="small" value={l.assumption} sx={{flex: 2}}
                                       onChange={e => setLine(i, {assumption: e.target.value})} slotProps={{htmlInput: {maxLength: 2000}}}/>
                            <Tooltip title="Remove line">
                                <span>
                                    <IconButton size="small" aria-label={`Remove ${l.name || 'line'}`} disabled={lines.length === 1}
                                                onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                                        <DeleteOutline fontSize="small"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    ))}
                    <Button size="small" startIcon={<Add/>} sx={{alignSelf: 'flex-start'}}
                            onClick={() => setLines([...lines, {id: null, name: '', baseCost: '0', seriesId: '', tier: 'estimated',
                                kind: 'cost', assumption: '', calibrationPct: null}])}>
                        Add line
                    </Button>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !ready}>Save model</Button>
            </DialogActions>
        </Dialog>
    );
};
